import { Box, Chip, Grid } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import FormSelect from '../../../../../components/FormSelect';
import getActiveLanguage from '../../../../../helpers/getActiveLanguage';
import useProducts from '../../../../../hooks/useProducts';
import usePropertyGroups from '../../../../../hooks/usePropertyGroups';
import FormButtons from '../FormButtons';
import PropertyItemsContainer from './PropertyItemsContainer';
import {
  defaultValues,
  ProductPropertyFormValues,
  validationSchema,
} from './types';
import LoadingState from '../../../../../components/LoadingState';

interface Props {
  onValidSubmit: (data: ProductPropertyFormValues) => void;
  onPrevClick: () => void;
  initialValues?: { groupIds: string[] };
}
function PropertyForm({ onValidSubmit, onPrevClick, initialValues }: Props) {
  const { t } = useTranslation('product');

  const { handleSubmit, control, watch, reset } =
    useForm<ProductPropertyFormValues>({
      defaultValues: initialValues || defaultValues(),
      resolver: validationSchema,
    });

  useEffect(() => reset(initialValues), [initialValues]);

  const onSubmit = handleSubmit(onValidSubmit);

  const groupIds = watch('groupIds');

  const { propertyGroups, fetchPropertyGroups, loading } = usePropertyGroups();

  const { productId } = useParams();
  const { updateProductPropertyGroups, groupsLoading } = useProducts();
  // todo lift up hooks functions to fix groups loading not firing or set loading in redux

  useEffect(() => {
    if (!propertyGroups.length) fetchPropertyGroups();
  }, [propertyGroups]);

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '100%',
      }}
    >
      <Box sx={{ py: 4 }}>
        <Grid container>
          <Grid item xs={2} />
          <Grid item xs={8}>
            <Controller
              name="groupIds"
              control={control}
              render={({ field, fieldState }) => (
                <FormSelect
                  label={t('properties.group.label')}
                  error={fieldState.invalid}
                  helperText={
                    fieldState.error
                      ? t(fieldState.error.message as any)
                      : undefined
                  }
                  options={propertyGroups.map(p => ({
                    id: p.id,
                    text: getActiveLanguage(p.translations).name,
                  }))}
                  multiple
                  onClose={() => {
                    if (productId) {
                      updateProductPropertyGroups({ productId, groupIds });
                    }
                  }}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(selected)
                        ? selected
                            .map(id => propertyGroups.find(g => g.id === id))
                            .map(value => (
                              <Chip
                                key={value?.id}
                                label={
                                  value
                                    ? getActiveLanguage(value.translations).name
                                    : ''
                                }
                              />
                            ))
                        : undefined}
                    </Box>
                  )}
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={2} />
        </Grid>
        {loading || groupsLoading ? (
          <LoadingState />
        ) : (
          <PropertyItemsContainer
            loading={groupsLoading}
            productId={productId}
          />
        )}
      </Box>

      <FormButtons onPrevClick={onPrevClick} />
    </Box>
  );
}
export default PropertyForm;
